import {
  FINANCE_DOCUMENT_TYPES,
  type FinanceDocumentType,
} from '@/config/work-items/financeTypes';

export { FINANCE_DOCUMENT_TYPES, type FinanceDocumentType };

export type FinanceTierRestrictions = {
  hiddenTabs?: unknown;
  hiddenActions?: unknown;
  disabledFeatures?: unknown;
  featureLimits?: unknown;
  includedFeatures?: unknown;
  tierName?: string | null;
  tierSlug?: string | null;
  minUsers?: number | null;
  maxUsers?: number | null;
  pricingModel?: string | null;
};

export type NormalizedFinanceTierRestrictions = {
  hiddenTabs: string[];
  hiddenActions: string[];
  disabledFeatures: string[];
  includedFeatures: string[];
  featureLimits: Record<string, unknown>;
  allowedDocumentTypes: FinanceDocumentType[] | null;
  tierName: string | null;
  tierSlug: string | null;
  minUsers: number | null;
  maxUsers: number | null;
  pricingModel: string | null;
};

export const FINANCE_DOC_TYPE_TO_TAB_ID: Record<FinanceDocumentType, string> = {
  invoice: 'invoices',
  quote: 'quotes',
  quote_request: 'quote-requests',
  billing_submission: 'billing-submissions',
  credit_note: 'credit-notes',
  debit_note: 'debit-notes',
  proforma: 'proforma',
  delivery_note: 'delivery-notes',
  waybill: 'waybills',
  sales_order: 'sales-orders',
  rma: 'rma',
  purchase_order: 'purchase-orders',
  grn: 'goods-received',
  supplier_credit: 'supplier-credits',
  supplier_quote: 'supplier-quotes',
  supplier_invoice: 'supplier-invoices',
  supplier_delivery_note: 'supplier-delivery-notes',
  purchase_requisition: 'purchase-requisitions',
  rfq: 'rfq',
  purchase_return: 'purchase-returns',
  purchase: 'purchases',
  // Expenses (unified 'expense' with item_subtype — bills + fuel/toll/etc)
  expense: 'expenses',
  work_order: 'work-orders',
  picking_slip: 'picking-slips',
  packing_slip: 'packing-slips',
  stock_adjustment: 'stock-adjustments',
  transfer_order: 'transfer-orders',
  // Property Finance — tabIds match documentHeaderConfig.ts
  levy_cycle: 'levy-cycles',
  meter_reading: 'meter-readings',
  utility_recovery: 'utility-recoveries',
  special_levy: 'special-levies',
  scheme_budget: 'scheme-budgets',
  reserve_fund: 'reserve-funds',
  deposit_holding: 'deposit-holdings',
  trust_transaction: 'trust-transactions',
  payment_dispute: 'payment-disputes',
  payment_plan: 'payment-plans',
  collection_case: 'collection-cases',
  invoice_followup: 'invoice-followups',
  credit_request: 'credit-requests',
  write_off: 'write-offs',
};

const TAB_ID_SYNONYMS: Record<string, string> = {
  invoices: 'invoices',
  invoice: 'invoices',
  quotes: 'quotes',
  quote: 'quotes',
  quote_requests: 'quote-requests',
  'quote-requests': 'quote-requests',
  credit_notes: 'credit-notes',
  'credit-notes': 'credit-notes',
  debit_notes: 'debit-notes',
  'debit-notes': 'debit-notes',
  proforma: 'proforma',
  delivery_notes: 'delivery-notes',
  'delivery-notes': 'delivery-notes',
  waybills: 'waybills',
  waybill: 'waybills',
  sales_orders: 'sales-orders',
  'sales-orders': 'sales-orders',
  rma: 'rma',
  purchase_orders: 'purchase-orders',
  'purchase-orders': 'purchase-orders',
  bills: 'bills',
  bill: 'bills',
  grn: 'goods-received',
  goods_received: 'goods-received',
  'goods-received': 'goods-received',
  supplier_credits: 'supplier-credits',
  'supplier-credits': 'supplier-credits',
  supplier_quotes: 'supplier-quotes',
  'supplier-quotes': 'supplier-quotes',
  requisitions: 'purchase-requisitions',
  purchase_requisitions: 'purchase-requisitions',
  'purchase-requisitions': 'purchase-requisitions',
  rfq: 'rfq',
  purchase_returns: 'purchase-returns',
  'purchase-returns': 'purchase-returns',
  work_orders: 'work-orders',
  'work-orders': 'work-orders',
  picking_slips: 'picking-slips',
  'picking-slips': 'picking-slips',
  packing_slips: 'packing-slips',
  'packing-slips': 'packing-slips',
  stock_adjustments: 'stock-adjustments',
  'stock-adjustments': 'stock-adjustments',
  transfer_orders: 'transfer-orders',
  'transfer-orders': 'transfer-orders',
};

const FEATURE_FLAG_SYNONYMS: Record<string, string> = {
  salesorders: 'sales_orders',
  deliverynotes: 'delivery_notes',
  purchaserequisitions: 'purchase_requisitions',
  purchaseorders: 'purchase_orders',
  suppliercredits: 'supplier_credits',
  supplierquotes: 'supplier_quotes',
  creditnotes: 'credit_notes',
  debitnotes: 'debit_notes',
  workorders: 'work_orders',
  pickingslips: 'picking_slips',
  packingslips: 'packing_slips',
  stockadjustments: 'stock_adjustments',
  transferorders: 'transfer_orders',
  goodsreceived: 'grn',
};

const FEATURE_TO_DOCUMENT_TYPES: Partial<Record<string, FinanceDocumentType[]>> = {
  quote_requests: ['quote_request'],
  credit_notes: ['credit_note'],
  debit_notes: ['debit_note'],
  proforma: ['proforma'],
  delivery_notes: ['delivery_note'],
  waybills: ['waybill'],
  sales_orders: ['sales_order'],
  purchase_orders: ['purchase_order'],
  supplier_credits: ['supplier_credit'],
  supplier_quotes: ['supplier_quote'],
  purchase_requisitions: ['purchase_requisition'],
  rfq: ['rfq'],
  purchase_returns: ['purchase_return'],
  work_orders: ['work_order'],
  picking_slips: ['picking_slip'],
  packing_slips: ['packing_slip'],
  stock_adjustments: ['stock_adjustment'],
  transfer_orders: ['transfer_order'],
  grn: ['grn'],
};

function normalizeKey(value: string): string {
  return value.toLowerCase().trim().replace(/[\s-]+/g, '_');
}

function toStringArray(value: unknown): string[] {
  if (!value) {
    return [];
  }

  if (typeof value === 'string') {
    try {
      const parsed = JSON.parse(value);
      if (Array.isArray(parsed)) {
        return parsed.filter((entry): entry is string => typeof entry === 'string');
      }
    } catch {
      return value
        .split(',')
        .map((entry) => entry.trim())
        .filter(Boolean);
    }
  }

  if (Array.isArray(value)) {
    return value.filter((entry): entry is string => typeof entry === 'string');
  }

  return [];
}

function toRecord(value: unknown): Record<string, unknown> {
  if (!value) {
    return {};
  }

  if (typeof value === 'string') {
    try {
      const parsed = JSON.parse(value);
      if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
        return parsed as Record<string, unknown>;
      }
    } catch {
      return {};
    }
  }

  if (typeof value === 'object' && !Array.isArray(value)) {
    return value as Record<string, unknown>;
  }

  return {};
}

function normalizeTabId(tabId: string): string {
  const normalized = normalizeKey(tabId);
  return TAB_ID_SYNONYMS[normalized] || normalized.replace(/_/g, '-');
}

function normalizeFeatureFlag(featureFlag: string): string {
  const normalized = normalizeKey(featureFlag);
  const condensed = normalized.replace(/_/g, '');
  return FEATURE_FLAG_SYNONYMS[normalized] || FEATURE_FLAG_SYNONYMS[condensed] || normalized;
}

function normalizeAllowedDocumentTypes(value: unknown): FinanceDocumentType[] | null {
  const allowedTypes = toStringArray(value)
    .map((entry) => normalizeKey(entry))
    .filter((entry): entry is FinanceDocumentType =>
      (FINANCE_DOCUMENT_TYPES as readonly string[]).includes(entry)
    );

  return allowedTypes.length > 0 ? allowedTypes : null;
}

export function normalizeFinanceTierRestrictions(
  restrictions?: FinanceTierRestrictions | null
): NormalizedFinanceTierRestrictions {
  const featureLimits = toRecord(restrictions?.featureLimits);

  return {
    hiddenTabs: Array.from(new Set(toStringArray(restrictions?.hiddenTabs).map(normalizeTabId))),
    hiddenActions: Array.from(new Set(toStringArray(restrictions?.hiddenActions).map(normalizeKey))),
    disabledFeatures: Array.from(new Set(toStringArray(restrictions?.disabledFeatures).map(normalizeFeatureFlag))),
    includedFeatures: Array.from(new Set(toStringArray(restrictions?.includedFeatures).map(normalizeFeatureFlag))),
    featureLimits,
    allowedDocumentTypes:
      normalizeAllowedDocumentTypes(featureLimits.allowed_document_types)
      ?? normalizeAllowedDocumentTypes(featureLimits.allowed_doc_types),
    tierName: restrictions?.tierName ?? null,
    tierSlug: restrictions?.tierSlug ?? null,
    minUsers: typeof restrictions?.minUsers === 'number' ? restrictions.minUsers : null,
    maxUsers: typeof restrictions?.maxUsers === 'number' ? restrictions.maxUsers : null,
    pricingModel: restrictions?.pricingModel ?? null,
  };
}

// Document types every finance package can open regardless of the tier's
// allowed_document_types allow-list. billing_submission is the intake
// artifact of the operational billing pipeline (job → submission →
// invoice) — blocking it strands the whole quote-to-cash chain on lower
// tiers even though the submissions themselves carry no sellable feature.
const UNIVERSALLY_ALLOWED_DOCUMENT_TYPES: readonly FinanceDocumentType[] = [
  'billing_submission',
];

export function getFinanceAllowedDocumentTypes(
  restrictions?: FinanceTierRestrictions | NormalizedFinanceTierRestrictions | null
): FinanceDocumentType[] {
  const normalized = normalizeFinanceTierRestrictions(restrictions as FinanceTierRestrictions);
  const hiddenTabs = new Set(normalized.hiddenTabs);
  const disabledFeatures = new Set(normalized.disabledFeatures);
  if (normalized.allowedDocumentTypes) {
    return Array.from(new Set([
      ...normalized.allowedDocumentTypes,
      ...UNIVERSALLY_ALLOWED_DOCUMENT_TYPES,
    ]));
  }

  return FINANCE_DOCUMENT_TYPES.filter((docType) => {
    const tabId = FINANCE_DOC_TYPE_TO_TAB_ID[docType];
    if (hiddenTabs.has(tabId)) {
      return false;
    }

    const matchingFeature = Object.entries(FEATURE_TO_DOCUMENT_TYPES).find(([, docTypes]) =>
      docTypes?.includes(docType)
    )?.[0];

    if (matchingFeature && disabledFeatures.has(matchingFeature)) {
      return false;
    }

    return true;
  });
}

export function isFinanceDocumentTypeAllowed(
  docType: string,
  restrictions?: FinanceTierRestrictions | NormalizedFinanceTierRestrictions | null
): docType is FinanceDocumentType {
  const normalizedDocType = normalizeKey(docType);
  if (!(FINANCE_DOCUMENT_TYPES as readonly string[]).includes(normalizedDocType)) {
    return false;
  }

  return getFinanceAllowedDocumentTypes(restrictions).includes(normalizedDocType as FinanceDocumentType);
}

export function isFinanceActionAllowed(
  actionId: string,
  restrictions?: FinanceTierRestrictions | NormalizedFinanceTierRestrictions | null
): boolean {
  const normalized = normalizeFinanceTierRestrictions(restrictions as FinanceTierRestrictions);
  return !normalized.hiddenActions.includes(normalizeKey(actionId));
}

export function isFinanceFeatureEnabled(
  featureId: string,
  restrictions?: FinanceTierRestrictions | NormalizedFinanceTierRestrictions | null
): boolean {
  const normalized = normalizeFinanceTierRestrictions(restrictions as FinanceTierRestrictions);
  const normalizedFeature = normalizeFeatureFlag(featureId);

  if (normalized.disabledFeatures.includes(normalizedFeature)) {
    return false;
  }

  const featureLimitValue = normalized.featureLimits[normalizedFeature];
  if (typeof featureLimitValue === 'boolean') {
    return featureLimitValue;
  }

  return true;
}

export function isFinanceStatementBatchingAllowed(
  restrictions?: FinanceTierRestrictions | NormalizedFinanceTierRestrictions | null
): boolean {
  return (
    isFinanceActionAllowed('invoice_batches', restrictions)
    && isFinanceFeatureEnabled('invoice_batches', restrictions)
    && isFinanceFeatureEnabled('statement_batching', restrictions)
  );
}

export function filterFinanceTabs<T extends { id: string } | { tabId: string }>(
  tabs: T[],
  restrictions?: FinanceTierRestrictions | NormalizedFinanceTierRestrictions | null
): T[] {
  const hiddenTabs = new Set(normalizeFinanceTierRestrictions(restrictions as FinanceTierRestrictions).hiddenTabs);
  return tabs.filter((tab) => {
    const tabIdentifier = 'id' in tab ? tab.id : tab.tabId;
    return !hiddenTabs.has(normalizeTabId(tabIdentifier));
  });
}

export function getFinanceUserCapLabel(
  restrictions?: FinanceTierRestrictions | NormalizedFinanceTierRestrictions | null
): string | null {
  const normalized = normalizeFinanceTierRestrictions(restrictions as FinanceTierRestrictions);
  if (normalized.maxUsers && normalized.minUsers && normalized.minUsers > 1) {
    return `${normalized.minUsers}-${normalized.maxUsers} users included`;
  }
  if (normalized.maxUsers) {
    return `Up to ${normalized.maxUsers} users included`;
  }
  if (normalized.minUsers) {
    return `From ${normalized.minUsers} users`;
  }
  return null;
}

export function getFinanceTierSummary(
  restrictions?: FinanceTierRestrictions | NormalizedFinanceTierRestrictions | null
): string | null {
  const normalized = normalizeFinanceTierRestrictions(restrictions as FinanceTierRestrictions);
  const parts = [normalized.tierName, getFinanceUserCapLabel(normalized)].filter(Boolean);
  return parts.length > 0 ? parts.join(' · ') : null;
}
